import {
  ALFEN_SYSTEM_STATUSES,
  BATTERY_MAJOR_VERSIONS,
  type BatteryConfigRow,
  type BatteryContactorStatus,
  CONTACTOR_STATUSES,
  type CurrentProgramId,
  WATCH_CONFIG_TABLE_NAME,
  type WatchAssetMessagesById,
  type WatchConfigOptions,
  type WatchConfigTableName,
  type WatchConfigTables,
  type WatchMessages,
  type WatchRoom,
} from '../hooks/useWatchSocket';
import {
  ASSET_LOCATIONS,
  type AssetLocation,
  INPUT_LOCATION_PROGRAMS,
  OPERATIONAL_STATE_LABELS,
  OUTPUT_LOCATION_PROGRAMS,
} from './hmi.constants';

export const formatNumber = (value: unknown): string | undefined =>
  typeof value === 'number' ? Math.round(value).toString() : undefined;

export const formatFloat = (value: unknown, decimals = 2): string | undefined =>
  typeof value === 'number' ? value.toFixed(decimals) : undefined;

export const formatTrend = (trend: number): string => {
  const value = Math.abs(trend).toFixed(0);
  const sign = value === '0' ? '' : trend > 0 ? '+' : '-';

  return `${sign}${value} %/h`;
};

export const getOnOffStatusKey = (value: unknown): string | undefined => {
  if (typeof value !== 'boolean') {
    return undefined;
  }
  return value ? 'on' : 'off';
};

export const getInputStatusKey = ({
  isAcBreakerClosed,
  contactorStatus,
}: {
  isAcBreakerClosed: boolean;
  contactorStatus: BatteryContactorStatus | undefined;
}): string => {
  if (!isAcBreakerClosed || contactorStatus === CONTACTOR_STATUSES.ALARM) {
    return 'error';
  }
  return contactorStatus === CONTACTOR_STATUSES.CLOSED ? 'on' : 'notInUse';
};

export const getClosedOpenStatusKey = (value: unknown): string | undefined => {
  if (typeof value !== 'boolean') {
    return undefined;
  }
  return value ? 'closed' : 'open';
};

export const formatPhaseValues = (...values: unknown[]): string =>
  values.map((value) => formatNumber(value) ?? '-').join(' / ');

export const getPowerFactor = ({
  activePower,
  reactivePower,
}: {
  activePower: unknown;
  reactivePower: unknown;
}): string => {
  if (typeof activePower !== 'number' || typeof reactivePower !== 'number') {
    return '-';
  }
  const apparentPower = Math.sqrt(activePower ** 2 + reactivePower ** 2);
  return apparentPower === 0 ? '-' : (activePower / apparentPower).toFixed(2);
};

export const isInUse = (option: WatchConfigOptions): boolean => Boolean(option.in_use);

export const isMobileXBattery = (majorVersion: number | undefined): boolean =>
  majorVersion === BATTERY_MAJOR_VERSIONS.MOBILE_X;

export const isModbusControlled = (option: WatchConfigOptions): boolean =>
  option.modbus_config_id !== undefined && option.modbus_config_id !== null;

export const getLocationLabel = ({ t, location }: { t: (key: string) => string; location: unknown }): string =>
  location === ASSET_LOCATIONS.OUTPUT ? t('locationOutput') : t('locationInput');

export const getProgramAssetLocation = (currentProgramId: CurrentProgramId | undefined): AssetLocation | undefined => {
  if (currentProgramId && OUTPUT_LOCATION_PROGRAMS.includes(currentProgramId)) {
    return ASSET_LOCATIONS.OUTPUT;
  }
  if (currentProgramId && INPUT_LOCATION_PROGRAMS.includes(currentProgramId)) {
    return ASSET_LOCATIONS.INPUT;
  }
  return undefined;
};

export const getAssetLocationLabel = ({
  t,
  location,
}: {
  t: (key: string) => string;
  location: AssetLocation | undefined;
}): string =>
  location === ASSET_LOCATIONS.OUTPUT
    ? t('locationOutput')
    : location === ASSET_LOCATIONS.INPUT
      ? t('locationInput')
      : '-';

export const getExternalAssetData = ({
  messagesByAssetId,
  assetId,
}: {
  messagesByAssetId: WatchAssetMessagesById | undefined;
  assetId: unknown;
}) => (assetId === undefined ? undefined : messagesByAssetId?.[String(assetId)]);

export const getBatteryConfigValue = ({
  batteryConfigRows,
  name,
}: {
  batteryConfigRows: BatteryConfigRow[] | undefined;
  name: string;
}): unknown => batteryConfigRows?.find((row) => row.name === name)?.val;

export const getStateLabel = ({
  t,
  value,
  labels,
  unknownKey = 'stateUnknown',
}: {
  t: (key: string) => string;
  value: unknown;
  labels: Record<number, string>;
  unknownKey?: string;
}): string => (typeof value === 'number' ? t(labels[value] ?? unknownKey) : '-');

export const getAssetOperationalState = ({
  t,
  asset,
  assetStateData,
}: {
  t: (key: string) => string;
  asset: WatchConfigOptions;
  assetStateData: WatchAssetMessagesById | undefined;
}): { stateValue: string; operationalState?: number } => {
  const assetState = getExternalAssetData({ messagesByAssetId: assetStateData, assetId: asset.id });
  const operationalState = assetState?.fields.operational_state;

  const stateValue = getStateLabel({
    t,
    value: operationalState,
    labels: OPERATIONAL_STATE_LABELS,
  });

  return {
    stateValue,
    operationalState: typeof operationalState === 'number' ? operationalState : undefined,
  };
};

/**
 * Checks whether a genset is currently active based on the bitmask integer
 * emitted in the watch state room (`state.active_gensets`).
 * Genset ID 1 corresponds to bit 0, ID 2 to bit 1, etc.
 */
export const isGensetActive = (activeGensets: number | undefined, gensetId: number): boolean => {
  if (activeGensets === undefined || gensetId < 1) {
    return false;
  }
  return (activeGensets & (1 << (gensetId - 1))) !== 0;
};

// modbus assets have no broken_state - connectivity is inferred from message freshness (see comments #1568)
export const MODBUS_CONNECTION_FRESHNESS_SECONDS = 15;

export const isModbusAssetConnected = ({
  tsMeasured,
  nowSeconds = Date.now() / 1000,
}: {
  tsMeasured: unknown;
  nowSeconds?: number;
}): boolean => typeof tsMeasured === 'number' && nowSeconds - tsMeasured <= MODBUS_CONNECTION_FRESHNESS_SECONDS;

// Keyed by enum member name (not array position) so reordering ALFEN_SYSTEM_STATUS can't desync the label.
const SYSTEM_STATUS_KEY_BY_NAME: Record<string, string> = {
  OFF: 'off',
  ON: 'on',
  BUSY: 'busy',
  WAITING_FOR_RESET: 'waitingForReset',
  SUSPENDED: 'suspended',
  RESET_BUSY: 'resetBusy',
  STARTING_UP: 'startingUp',
};

// ALFEN_SYSTEM_STATUSES is name->number (no reverse index like a real enum), so invert it once here.
const ALFEN_SYSTEM_STATUS_NAME_BY_VALUE: Record<number, string> = Object.fromEntries(
  Object.entries(ALFEN_SYSTEM_STATUSES).map(([name, value]) => [value, name])
);

export const systemStatusKey = (status: number): string =>
  SYSTEM_STATUS_KEY_BY_NAME[ALFEN_SYSTEM_STATUS_NAME_BY_VALUE[status]] ?? 'off';

export enum ALFEN_SYSTEM_MODE {
  STANDBY = 0,
  PQ = 1,
  PF = 2,
  QU = 3,
  PF_QU_PQ = 4,
  PEAK_SHAVING = 5,
  MICROGRID = 6,
}

// Keyed by enum member name (not array position) so reordering ALFEN_SYSTEM_MODE can't desync the label.
const SYSTEM_MODE_KEY_BY_NAME: Record<string, string> = {
  STANDBY: 'standby',
  PQ: 'pq',
  PF: 'pf',
  QU: 'pfQu',
  PF_QU_PQ: 'pfQuPq',
  PEAK_SHAVING: 'peakshaving',
  MICROGRID: 'microgrid',
};

export const systemModeKey = (mode: number): string => SYSTEM_MODE_KEY_BY_NAME[ALFEN_SYSTEM_MODE[mode]] ?? 'standby';

// Keyed by enum member name (not array position) so reordering CONTACTOR_STATUSES can't desync the label.
const CONTACTOR_STATUS_KEY_BY_NAME: Record<string, string> = {
  UNKNOWN: 'unknown',
  CLOSED: 'closed',
  OPEN: 'open',
  INTERMEDIATE: 'intermediate',
  ALARM: 'alarm',
  ABORTED: 'aborted',
  NOT_PRESENT: 'notPresent',
};

// CONTACTOR_STATUSES (imported) is name->number (no reverse index like a real enum), so invert it once here.
const CONTACTOR_STATUS_NAME_BY_VALUE: Record<number, string> = Object.fromEntries(
  Object.entries(CONTACTOR_STATUSES).map(([name, value]) => [value, name])
);

export const getContactorStatusKey = (status: number): string =>
  CONTACTOR_STATUS_KEY_BY_NAME[CONTACTOR_STATUS_NAME_BY_VALUE[status]] ?? 'unknown';

// Shared by BatteryTab and PowerTab so contactor dot colors can't drift between the two tabs.
export const CONTACTOR_STATUS_DOT_COLORS: Record<string, string> = {
  unknown: 'action.disabled',
  closed: 'success.main',
  open: 'action.disabled',
  intermediate: 'warning.main',
  alarm: 'error.main',
  aborted: 'error.main',
  notPresent: 'action.disabled',
};

export const REQUIRED_WATCH_ROOMS: WatchRoom[] = [
  'state',
  'timeseries',
  'components/input_data',
  'components/container_data',
  'metadata',
];

export const INITIAL_WATCH_CONFIG_TABLES: WatchConfigTableName[] = [
  WATCH_CONFIG_TABLE_NAME.BatteryConfig,
  WATCH_CONFIG_TABLE_NAME.ExternalGensetConfig,
  WATCH_CONFIG_TABLE_NAME.LoadBankConfig,
  WATCH_CONFIG_TABLE_NAME.ExternalSolarInverterConfig,
];

/** True once the watch has delivered every room and config table the overview diagram is built from. */
export const hasInitialWatchData = (messages: WatchMessages, configTables: WatchConfigTables): boolean =>
  REQUIRED_WATCH_ROOMS.every((room) => messages[room] !== undefined) &&
  INITIAL_WATCH_CONFIG_TABLES.every((table) => configTables[table] !== undefined);
